import { ChangeEvent, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { HTTPRequestStatus, MaskTypes } from "../../../utils/models";
import FormLayout from "../../../components/FormLayout";
import { Typography, Theme, Grid, Paper } from "@mui/material";
import RadioQuestions from "../../../components/RadioQuestions";
import TextInput from "../../../components/TextInput";
import { omitDollar, toDollar, toFloatString, toIntString } from "../../../utils/dataConverter";
import { useRefund } from "../../../hooks/TaxCalculations";
import { useDispatch, useSelector } from "react-redux";
import { paymentSlice } from "../../../store/reducer/payment";
import { TaxCalculations } from "../../../model/tax_calculations";
import { UserChoice } from "../../../model/final_balance/user_choice";
import { Refund } from "../../../model/final_balance/refund";
import { RefundType } from "../../../model/final_balance/refund_types";
import { ValidatorForm } from "react-material-ui-form-validator";
import CustomButton from "../../../components/CustomButton";
import { RootState } from "../../../store/index";
import { SuccessAlert, ErrorAlert } from "../../../components/Alerts";
import LineLoader from "../../../components/Loaders/LineLoader/LineLoader";
import { usePaymentContainerContext } from "../PaymentContainer";
import MaskTextInput from "../../../components/MaskTextInput";


function GenericAlert({ status, message }: { status: HTTPRequestStatus, message: string }) {
  if (status === HTTPRequestStatus.Error) {
    return <ErrorAlert>{message}</ErrorAlert>
  } else if (status === HTTPRequestStatus.Success) {
    return <SuccessAlert message={message}/>
  } else {
    return null;
  }
}

const RefundInformation = () => {
  const {
    data: { collection_year },
  } = useSelector((appState: any) => appState.user);
  const { refundUpdateStatus, alertMessage } = useSelector((state: RootState) => state.payment);
  const { taxCalculations } = usePaymentContainerContext();

  // TODO may want to dispatch fetchRefund action
  const refund: Refund = useRefund();

  // TODO set these from redux store
  // TODO changeRefund if balance is negative prior to this?
  const [refundOption, setRefundOption] = useState(refund.type);
  const [refundAmount, setRefundAmount] = useState(refund.refund_amount as number | string);
  const [creditAmount, setCreditAmount] = useState(refund.credit_amount);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(paymentSlice.actions.changeRefund({
      refundAmount: Number(refundAmount),
      creditAmount: creditAmount
    }));
  }, [refundAmount, creditAmount, dispatch]);

  const radioOptions: UserChoice[] = [
    {
      id: RefundType.Refund,
      label: `Please refund the entire amount to me. `,
    },
    {
      id: RefundType.Credit,
      label: `Please credit the entire amount to my ${
        Number(collection_year) + 1
      } return.`,
    },
    {
      id: RefundType.Partial,
      label: `Please apply some to tax year ${
        Number(collection_year) + 1
      } and refund the rest. Here’s how much I'd like to receive as a refund:`,
    },
  ];

  const handleChangeRadio = (event: ChangeEvent<HTMLInputElement>) => {
    let option = event.target.value as RefundType;
    setRefundOption(option);
    if (option === RefundType.Refund) {
      setRefundAmount(Math.abs(taxCalculations.final_balance));
      setCreditAmount(0);
    } else if (option === RefundType.Credit) {
      setRefundAmount(0);
      setCreditAmount(Math.abs(taxCalculations.final_balance));
    } else {
      // Do nothing, since amounts will be updated when number input is changed
    }
  };

  const handleSplitRefund = (event: ChangeEvent<HTMLInputElement>) => {
    let requestedAmount = Number(toFloatString(event.target.value));

    if (requestedAmount > Math.abs(taxCalculations.final_balance)) {
      setRefundAmount(Math.abs(taxCalculations.final_balance));;
      setCreditAmount(0);
    } else if (isNaN(requestedAmount)) {
      setRefundAmount(0);
      setCreditAmount(Math.abs(taxCalculations.final_balance));
    } else {
      setRefundAmount(omitDollar(event.target.value));
      setCreditAmount(Math.abs(taxCalculations.final_balance) - requestedAmount);
    }
  };

  return (
    <>
      {(refundUpdateStatus === HTTPRequestStatus.Requesting)
        ? <LineLoader />
        : <FormLayout
      title={"Tax Refund"}
      subtitle={"Payment"}
      description={"Congrats on the refund. Select from the options below for how you'd like to use your refund."}
      className=""
    >
      <Grid container spacing={4} sx={{ paddingTop: '32px'}}>
        { refundUpdateStatus !== HTTPRequestStatus.Success &&
          <Grid
            size={{
              xs: 12,
              md: 7
            }}>
            <Paper elevation={0} className='paper-container'>
              <RadioQuestions
                question=""
                radioOptions={radioOptions}
                handleChangeRadio={handleChangeRadio}
                radioValue={refundOption}
              />
              <br />
              { /* This really isn't a form that gets submitted. confirmRefund action is dispatched using "Next" button */}
              {refundOption === RefundType.Partial && (
                <ValidatorForm onSubmit={() => {}} >
                  <Box className='text-wrapper'>
                    <Box className='input'>
                    <MaskTextInput
                        name="refund"
                        maskType={MaskTypes.Dollar}
                        id="filled-helperText"
                        label="Refund"
                        placeholder="Amount to refund"
                        variant="filled"
                        value={refundAmount}
                        onChange={handleSplitRefund}
                      />
                    </Box>
                  </Box>
                </ValidatorForm>
              )}
            </Paper>
          </Grid>
        }
        <Grid
          size={{
            xs: 12,
            md: 5
          }}>
          <Paper elevation={0} className='paper-container'>
            <h3>Your Refund</h3>
            <div className='total-row'>
              <span className='total-label'>Refund:</span>
              <span className='total-amount' style={{color: 'green'}}>{toDollar(toFloatString(refundAmount))}</span>
            </div>
            <div className='total-row'>
              <span className='total-label'>Carryover:</span>
              <span className='total-amount' style={{color: 'green'}}>{toDollar(toFloatString(creditAmount))}</span>
            </div>
          </Paper>
          <Grid className='form-container' size={12}>
            <CustomButton
              variant="contained"
              disableElevation={true}
              label={"Finish"}
              onClick={() => dispatch(paymentSlice.actions.confirmRefund())}
              style={{ width: "100%" }}
            />
          </Grid>
        </Grid>
      </Grid>
      <GenericAlert
        status={refundUpdateStatus}
        message={alertMessage}
      />
    </FormLayout>
    }
    </>
  );
}
/*
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      marginTop: 16,
    },
    paperContainer: {
      padding: 32,
      borderRadius: 15,
    },
    formRow: { marginBottom: 8 },
    radioGrpRow: {
      marginTop: 16,
      marginBottom: 16,
      marginLeft: 11,
    },
    hr: {
      marginTop: 16,
      marginBottom: 16,
      border: "1px solid rgba(0, 0, 0, 0.5)",
    },
    radioGrplabel: {
      fontStyle: "normal",
      fontWeight: "normal",
      fontSize: 16,
      lineHeight: "140%",
      color: "#383D39",
    },
    helpIcon: {
      color: "#898989",
      fontSize: 15,
      verticalAlign: "top",
      marginTop: 14,
      marginLeft: 10,
      cursor: "pointer",
    },
    totalRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    totalLabel: {
      flex: 1,
      display: "flex",
      fontSize: 16,
    },
    totalAmount: {
      flex: 1,
      display: "flex",
      fontSize: 16,
      fontWeight: 700,
    },
    card: {
      paddingTop: "15px",
      paddingBottom: "15px",
    },
    input: {
      width: 250,
      paddingTop: "15px",
      paddingBottom: "15px",
    },
    carryOver: {
      display: "flex",
    },
    amount: {
      color: "rgb(109 131 51)",
      fontWeight: 700,
    },
    textWraper: {
      paddingLeft: "25px",
    },
  })
);*/

export default RefundInformation;
